/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-10-20 15:23:50
 */
// AI识别模块TypeScript类型定义
import { RefObject } from 'react'
import { XCImageSelectionEditorRef } from '../../components/ImageSelectionEditor'

/**
 * 识别状态常量映射
 */
export const XC_RECOGNITION_STATUS = {
  WAITING: 'waiting',
  RECOGNIZING: 'recognizing',
  COMPLETED: 'completed'
} as const

/**
 * 识别状态类型
 */
export type XCRecognitionStatus = typeof XC_RECOGNITION_STATUS[keyof typeof XC_RECOGNITION_STATUS]

/**
 * 识别结果接口
 */
export interface XCRecognitionResult extends RecognitionRecord {
  msg?: string
}

// 识别记录数据类型（基于API返回结构）
export interface RecognitionRecord {
  id?: number // 记录唯一标识符
  isSelected?: boolean // 是否选中
  url?: string // 隐患照片
  photoFilesAfter?: string[] // 整改后照片
  nickName?: string // 拍摄人
  createTime?: string // 检查日期
  RectificationManager?: string // 整改责任人
  ConstructioUnit?: string // 施工单位
  deadline?: string // 整改期限
  finishTime?: string // 完成时间
  comment?: string // 备注

  thinking?: string // AI 思考过程或分析结果
  uuid?: string // 通用唯一标识符
  userID?: string // 用户标识符
  groupID?: string // 组织/团队标识符
  isDelete?: boolean // 删除标记
  obj?: string // 对象信息或附加数据
  projectName?: string // 项目名称
  hazardSourceList?: Array<{ name: string; val: number }> // 隐患生成支持的来源
  selectedHazardSource?: number // hazardSource参数取值的index  0:llm 1:水印
  // 识别项
  standardList?: string[] // 相关依据
  potentialHazardList?: string[] // 存在的隐患
  monomerName?: string // 单体名称
  RectificationMeasure?: string // 整改措施
  questionType?: string // 问题类型
  standardListContent?: string[] // 水印内容-相关依据
  potentialHazardListContent?: string[] // 水印内容-存在的隐患
  monomerNameContent?: string // 水印内容-单体名称
  RectificationMeasureContent?: string // 水印内容-整改措施
  questionTypeContent?: string // 水印内容-问题类型

  // 表格显示相关字段
  key?: number | string // 表格行的唯一键值
  status?: XCRecognitionStatus // 识别状态
  isNewRecord?: boolean // 新记录标识，用于插入新航动画效果
  isAnimationRow?: boolean // 动画行标识，用于判断是否需展示识别中
  bbox?: number[][]
  // 扩展：用于固定行在批量识别时的多图预览
  selectedPreviewImages?: string[] // 批量模式下用于第一行展示的预览图（最多3张）
  batchModeTip?: string // 批量识别模式提示文案，例如“批量识别中”
}

/**
 * 导出数据接口
 */
export interface XCRecognitionExportDataResult {
  exportURL: string
  fileName?: number
}

/**
 * 选择区域信息接口
 */
export interface XCSelectionInfo {
  x: number
  y: number
  width: number
  height: number
}

/**
 * 安全隐患场景接口
 */
export interface XCSafetyScenario {
  id: string
  name: string
  description?: string
  selected: boolean
}

/**
 * 筛选参数接口
 */
export interface XCFilterParams {
  keywords?: string
  questionTypeList?: string[]
  monomerName?: string
  startTime?: number
  endTime?: number
  departmentUsers: any[]
  departments: any[]
  isAll?: boolean
}

/**
 * 批量识别进度
 */
export interface XCBatchProgress {
  total: number
  completed: number
  failed: number
  inFlight: number
}

/**
 * AI识别状态接口
 */
export interface AISecurityState {
  selectedImages: string[]
  recognitionResult?: XCRecognitionResult
  recognitionStatus: XCRecognitionStatus
  selectionInfo?: XCSelectionInfo[]
  /** 选中的安全场景 */
  selectedSafetyScenarios: XCSafetyScenario[]
  /** 安全场景列表 */
  safetyScenarioList: XCSafetyScenario[]
  userId: string | null | undefined
  groupId: string | null
  isGroupVIP: boolean
  myRole: string | number
  imageSelectionEditorRef?: RefObject<XCImageSelectionEditorRef>
  /** 记住本次选择 */
  rememberChoice: boolean
  /** 安全场景选择Modal状态 */
  showSafetyScenarioModal: boolean
  /** 筛选参数 */
  filterParams: XCFilterParams
  /** 是否处于批量识别模式 */
  isBatchMode: boolean
  /** 批量识别进度 */
  batchProgress: XCBatchProgress
}

/**
 * AI识别操作接口
 */
export interface AISecurityActions {
  /** 设置选中的图片 */
  setSelectedImages: (url: string[]) => void
  setRecognitionResult: (result?: XCRecognitionResult) => void
  setRecognitionStatus: (status: XCRecognitionStatus) => void
  setSelectionInfo: (info: XCSelectionInfo[]) => void
  /** 设置图片选择编辑器的引用 */
  setImageSelectionEditorRef: (
    ref: RefObject<XCImageSelectionEditorRef>
  ) => void
  /** 设置选中的安全场景 */
  setSelectedSafetyScenarios: (scenarios: XCSafetyScenario[]) => void
  /** 设置安全场景列表 */
  setSafetyScenarioList: (scenarios: XCSafetyScenario[]) => void

  /** 开始识别 */
  startRecognition: () => void

  /** 更新参考标准 */
  updateReferenceStandard: (value: string[]) => void

  /** 更新思考过程 */
  updateThinkingProcess: (value: string) => void

  /** 更新记住本次选择 */
  setRememberChoice: (value: boolean) => void

  /** 安全场景Modal操作 */
  setShowSafetyScenarioModal: (visible: boolean) => void

  /** 设置筛选参数 */
  setFilterParams: (params: XCFilterParams) => void

  /** 重置筛选参数 */
  resetFilterParams: () => void

  /** 批量模式 */
  setIsBatchMode: (isBatch: boolean) => void
  setBatchProgress: (progress: XCBatchProgress) => void
}

/**
 * Context完整接口
 */
export interface XCAISecurityContext
  extends AISecurityState,
    AISecurityActions {}

/**
 * AI识别头部组件Props
 */
export interface AISecurityHeaderProps {
  onRecognitionHistory?: () => void
  loading?: boolean
}
